import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import type { HalfModalType } from '@/components/layout/modals/types';
import { Button } from '@/components/ui/button/Button';
import { IconClose } from '@/components/ui/icons/IconClose';
import { Z_INDEX } from '@/constants/z-index';
import { cn } from '@/utils/cn';

export const HalfModalBase = ({
  isOpen = false,
  onClose,
  cancelable = false,
  withSidePadding = true,
  children,
}: HalfModalType) => {
  const t = useTranslations();

  // Escape キーで閉じる（背景クリックと同じ挙動）。onClose 未指定時は何もしない。
  useEffect(() => {
    if (!isOpen || !onClose) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className='pointer-events-none fixed inset-0 left-1/2 top-0 flex h-full w-full -translate-x-1/2 items-end justify-center md:w-md-base lg:w-lg-base'
          style={{ zIndex: Z_INDEX.MODAL_BASE }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='pointer-events-auto absolute left-0 top-0 z-0 h-full w-full touch-none bg-bk-opacity-20'
            onClick={onClose}
          />
          <motion.div
            role='dialog'
            aria-modal='true'
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={cn(
              'pointer-events-auto relative z-20 w-full rounded-t-2xl bg-white pb-10 pt-8 lg:ml-[20rem]',
              withSidePadding && 'px-5',
            )}
          >
            {cancelable && (
              <Button
                variant='iconOnly'
                IconComponent={IconClose}
                aria-label={t('common-close')}
                className='absolute right-2x top-3x z-30 h-4 w-4'
                onClick={onClose}
              />
            )}
            <div className='text-center'>{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
